/// useEditorWorker: React hook for editor WASM worker lifecycle.
/// Manages OffscreenCanvas transfer, WebGL initialization, input forwarding,
/// and provides editing commands (undo, redo, delete, copy, paste, zoom).

import { useRef, useEffect, useCallback, useState } from "react";
import { getWorkerManager, type WorkerRole } from "@/lib/WorkerManager";
import type { EditorType } from "@/types/editor";
import type { InputEvent } from "@lib/InputEvent.js";
import { useEditorStore } from "@/stores/editorStore";

/** Editor worker types that this hook manages. */
type EditorRole = "bpmn-editor" | "node-editor";

/**
 * Monotonic counter for generating unique worker IDs.
 * Date.now() is insufficient because multiple hook instances created in the
 * same render pass (e.g. two node-editor tabs mounting simultaneously during
 * a panel split) would receive identical timestamps, causing WorkerHost to
 * throw "already registered".
 */
let workerIdCounter = 0;
function generateWorkerId(role: EditorRole): string {
  return `${role}-${Date.now()}-${workerIdCounter++}`;
}

interface UseEditorWorkerOptions {
  /** Which editor to use. */
  editorType: EditorType;
  /** Auto-start the worker on mount (default: false). */
  autoStart?: boolean;
}

interface UseEditorWorkerReturn {
  /** Whether the editor worker is ready. */
  isReady: boolean;
  /** Any error from the worker. */
  error: string | null;
  /** Initialize the editor with an OffscreenCanvas from a canvas element. */
  initEditor: (canvasElement: HTMLCanvasElement) => void;
  /** Stop and terminate the editor worker. */
  stop: () => void;
  /** Forward a resize event. */
  resize: (width: number, height: number) => void;
  /** Forward an input event to the editor. */
  forwardInput: (event: InputEvent) => void;
  /** Execute an editing command. */
  undo: () => void;
  redo: () => void;
  deleteSelected: () => void;
  copy: () => void;
  cut: () => void;
  paste: () => void;
  selectAll: () => void;
  /** Add a BPMN/Node element at a position. */
  addElement: (elementType: string, x: number, y: number, label: string) => void;
  /** Set zoom level. */
  zoom: (level: number, centerX?: number, centerY?: number) => void;
  /** Fit the view to show all content. */
  fitToView: () => void;
  /** Load a document into the editor. */
  loadDocument: (content: string, format: "bpmn" | "nodeflow") => void;
  /** Save the current document. */
  saveDocument: () => void;
  /** Import an N8N workflow JSON into the node editor. */
  importN8N: (content: string) => void;
  /** Set a property on an element (from the properties panel). */
  setProperty: (elementId: string, properties: Record<string, unknown>) => void;
  /** Trigger auto-layout on the diagram. */
  autoLayout: (selectedOnly?: boolean, algorithm?: number) => void;
  /** Add a connection between two elements. */
  addConnection: (
    sourceId: number,
    targetId: number,
    sourceSocket?: string,
    targetSocket?: string,
  ) => void;
  /** Run BPMN validation and get results via validationResults event. */
  validate: () => void;
  /** Start inline text editing on a BPMN element. */
  startInlineEdit: (groupId: number) => void;
  /** Commit inline text edit with the given text. */
  commitInlineEdit: (text: string) => void;
  /** Cancel inline text edit. */
  cancelInlineEdit: () => void;
  /** Set condition expression on a BPMN sequence flow. */
  setFlowCondition: (connectionId: number, expression: string) => void;
  /** Set or unset default flow marker on a BPMN sequence flow. */
  setDefaultFlow: (connectionId: number, isDefault: boolean) => void;
  /** Set multi-instance marker type on a BPMN task (0=None, 1=Parallel, 2=Sequential). */
  setMultiInstance: (groupId: number, multiInstanceType: number) => void;
  /** Attach a boundary event to a parent activity at a border position (0-1). */
  attachBoundaryEvent: (eventId: number, parentId: number, position?: number) => void;
  /** Detach a boundary event from its parent activity. */
  detachBoundaryEvent: (eventId: number) => void;
  /** Toggle subprocess expand/collapse. */
  toggleSubprocess: (subprocessId: number) => void;
  /** Create a pool container at the given position. */
  createPool: (x: number, y: number, name?: string) => void;
  /** Create a lane inside a pool. */
  createLane: (poolId: number, name?: string) => void;
  /** Add a dynamic socket to a node element. */
  addSocket: (
    groupId: number,
    socketId: string,
    name: string,
    dataType: string,
    isInput: boolean,
  ) => void;
  /** Remove a dynamic socket from a node element. */
  removeSocket: (groupId: number, socketId: string, isInput: boolean) => void;
  /** Group selected nodes into a labeled container. */
  groupSelected: (label?: string) => void;
  /** Dissolve a node group back to individual nodes. */
  dissolveGroup: (groupId: number) => void;
  /** Set the active tool mode on the WASM side. */
  setToolModeOnWorker: (mode: number) => void;
  /** Cancel an active connection drag preview. */
  cancelConnectDrag: () => void;
  /** Create an element at a position and connect it from a source element. */
  createAndConnect: (
    elementType: string,
    x: number,
    y: number,
    label: string,
    sourceId: number,
    sourceSocket?: string,
  ) => void;
  /** Toggle collapse/expand on a node group (Phase 26, Plan 02). */
  toggleCollapse: (groupId: number) => void;
  /** Query which group is hovered for tooltip display (Phase 26, Plan 02). */
  queryHoveredGroup: (screenX: number, screenY: number) => void;
  /** The worker ID (for event subscription). */
  workerId: string;
}

/**
 * React hook for managing an editor WASM worker.
 * Handles OffscreenCanvas transfer, WebGL init, and provides
 * a typed API for editing commands and input forwarding.
 *
 * StrictMode-safe: the worker survives React 18 StrictMode's
 * mount-unmount-remount cycle. The worker is only terminated
 * when the hook is explicitly stopped or on true unmount
 * (detected via a deferred cleanup check).
 */
export function useEditorWorker(options: UseEditorWorkerOptions): UseEditorWorkerReturn {
  const { editorType } = options;
  const role: EditorRole = editorType === "bpmn" ? "bpmn-editor" : "node-editor";
  const workerRef = useRef<Worker | null>(null);
  const workerIdRef = useRef<string>(generateWorkerId(role));
  const cleanupRef = useRef<(() => void) | null>(null);
  const mountedRef = useRef(false);
  const canvasTransferredRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createWorker = useCallback((): void => {
    // If worker already exists, skip creation
    if (workerRef.current) return;

    const manager = getWorkerManager();
    const wid = workerIdRef.current;

    let worker: Worker;
    if (role === "bpmn-editor") {
      worker = new Worker(new URL("../workers/bpmn-editor.worker.ts", import.meta.url), {
        type: "module",
      });
    } else {
      worker = new Worker(new URL("../workers/node-editor.worker.ts", import.meta.url), {
        type: "module",
      });
    }

    workerRef.current = worker;
    console.log(`[useEditorWorker] Created ${role} worker, id=${wid}`);

    worker.onerror = (ev) => {
      console.error(`[${role}] worker.onerror:`, ev);
      setError(String(ev.message ?? "Worker load error"));
    };

    worker.addEventListener("messageerror", (ev) => {
      console.error(`[${role}] worker messageerror:`, ev);
      setError("Worker message deserialization error");
    });

    manager.registerWorker({ workerId: wid, role: role as WorkerRole, worker });

    let readyReceived = false;

    const unsubReady = manager.onEditorEvent("ready", (sourceId) => {
      if (sourceId === wid) {
        console.log(`[${role}] ready event received`);
        readyReceived = true;
        setIsReady(true);
        setError(null);
      }
    });
    const unsubError = manager.onEditorEvent("error", (sourceId, env) => {
      if (sourceId === wid) {
        console.error(`[${role}] error event:`, env.payload);
        setError(String(env.payload?.message ?? "Unknown error"));
      }
    });

    // Handle documentSaved event: trigger browser file download
    const unsubSaved = manager.onEditorEvent("documentSaved", (sourceId, env) => {
      if (sourceId !== wid) return;
      const payload = env.payload as { content: string; format: string } | undefined;
      if (!payload?.content) return;
      const ext = payload.format === "bpmn" ? ".bpmn" : ".json";
      const mimeType = payload.format === "bpmn" ? "application/xml" : "application/json";
      const blob = new Blob([payload.content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `diagram${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    });

    // Handle collapseChanged event via MessageEnvelope: update editorStore collapse state (Phase 26, Plan 02; Phase 30 editor: prefix)
    const unsubCollapse = manager.onEditorEvent("editor:collapseChanged", (sourceId, env) => {
      if (sourceId !== wid) return;
      const payload = env.payload as { groupId: number; collapsed: boolean; childNames: string[] };
      const state = useEditorStore.getState();
      const tab = state.tabs.find(
        (t) => state.editorStates.get(t.id)?.workerId === wid,
      );
      if (tab) {
        useEditorStore.getState().setCollapseState(tab.id, payload.groupId, payload.collapsed, payload.childNames);
      }
    });

    // Handle hoveredGroupChanged event via MessageEnvelope (Phase 30 editor: prefix)
    const unsubHovered = manager.onEditorEvent("editor:hoveredGroupChanged", (sourceId, env) => {
      if (sourceId !== wid) return;
      const payload = env.payload as {
        groupId: number; collapsed: boolean; childNames: string[]; screenX: number; screenY: number;
      } | null;
      const state = useEditorStore.getState();
      const tab = state.tabs.find(
        (t) => state.editorStates.get(t.id)?.workerId === wid,
      );
      if (tab) {
        useEditorStore.getState().setHoveredGroup(tab.id, payload);
      }
    });

    // Diagnostic: if worker doesn't respond within 10s, something is wrong
    const diagTimer = setTimeout(() => {
      if (!workerRef.current || readyReceived) return;
      console.warn(
        `[${role}] Worker did not become ready within 10s. Check worker console for errors.`,
      );
    }, 10000);

    cleanupRef.current = () => {
      unsubReady();
      unsubError();
      unsubSaved();
      unsubCollapse();
      unsubHovered();
      clearTimeout(diagTimer);
    };
  }, [role]);

  // StrictMode-safe cleanup: use a deferred check to distinguish
  // StrictMode remount from real unmount. In StrictMode, React
  // runs cleanup then immediately re-runs the effect. We defer
  // the actual termination so the remount can cancel it.
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      // Defer termination -- if StrictMode remounts the component,
      // mountedRef will be set back to true before this fires.
      const capturedWorker = workerRef.current;
      const capturedWid = workerIdRef.current;
      const capturedCleanup = cleanupRef.current;

      setTimeout(() => {
        if (mountedRef.current) return; // StrictMode remounted -- keep the worker alive

        // Real unmount -- terminate the worker
        capturedCleanup?.();
        if (capturedWorker) {
          capturedWorker.terminate();
        }
        // Only clear the ref if it still points to the same worker
        if (workerRef.current === capturedWorker) {
          workerRef.current = null;
        }
        const manager = getWorkerManager();
        manager.unregisterWorker(capturedWid);
      }, 50);
    };
  }, []);

  const initEditor = useCallback(
    (canvasElement: HTMLCanvasElement) => {
      // Create worker if not already running
      createWorker();

      // Skip if canvas was already transferred (StrictMode remount
      // or re-render). transferControlToOffscreen can only be called
      // once per canvas element.
      if (canvasTransferredRef.current) return;

      try {
        const dpr = window.devicePixelRatio || 1;
        const width = canvasElement.clientWidth;
        const height = canvasElement.clientHeight;

        // Set intrinsic canvas dimensions before transferring control
        // (OffscreenCanvas inherits these; without this the buffer stays 300x150)
        canvasElement.width = Math.round(width * dpr);
        canvasElement.height = Math.round(height * dpr);

        const offscreen = canvasElement.transferControlToOffscreen();

        workerRef.current?.postMessage(
          {
            type: "initEditor",
            canvas: offscreen,
            width,
            height,
            dpr,
          },
          [offscreen],
        );
        canvasTransferredRef.current = true;
      } catch (err) {
        // transferControlToOffscreen throws if already transferred.
        // This is expected in StrictMode where the effect re-runs
        // but the canvas already had its control transferred.
        console.warn(`[${role}] Canvas control already transferred (StrictMode remount):`, err);
        canvasTransferredRef.current = true;
      }
    },
    [createWorker, role],
  );

  const stop = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    const manager = getWorkerManager();
    const wid = workerIdRef.current;
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    manager.unregisterWorker(wid);
    setIsReady(false);
    setError(null);
    canvasTransferredRef.current = false;
    workerIdRef.current = generateWorkerId(role);
  }, [role]);

  const resize = useCallback((width: number, height: number) => {
    if (!workerRef.current) return;
    const dpr = window.devicePixelRatio || 1;
    workerRef.current.postMessage({ type: "resize", width, height, dpr });
  }, []);

  const forwardInput = useCallback((event: InputEvent) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "input", payload: event });
  }, []);

  // Editing commands (direct postMessage to worker, not MessageEnvelope)
  const sendEditCommand = useCallback((action: string) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "edit", action });
  }, []);

  const undo = useCallback(() => sendEditCommand("undo"), [sendEditCommand]);
  const redo = useCallback(() => sendEditCommand("redo"), [sendEditCommand]);
  const deleteSelected = useCallback(() => sendEditCommand("delete"), [sendEditCommand]);
  const copy = useCallback(() => sendEditCommand("copy"), [sendEditCommand]);
  const cut = useCallback(() => sendEditCommand("cut"), [sendEditCommand]);
  const paste = useCallback(() => sendEditCommand("paste"), [sendEditCommand]);
  const selectAll = useCallback(() => sendEditCommand("selectAll"), [sendEditCommand]);

  const addElement = useCallback((elementType: string, x: number, y: number, label: string) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "addElement", elementType, x, y, label });
  }, []);

  // Zoom commands (direct postMessage to worker, not MessageEnvelope)
  const zoom = useCallback((level: number, centerX?: number, centerY?: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "zoom", level, centerX, centerY });
  }, []);

  const fitToView = useCallback(() => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "fitToView" });
  }, []);

  const loadDocument = useCallback((content: string, format: "bpmn" | "nodeflow") => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "loadDocument", content, format });
  }, []);

  const saveDocument = useCallback(() => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "saveDocument" });
  }, []);

  const importN8N = useCallback((content: string) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "importN8N", content });
  }, []);

  // Property change command (from Properties panel -> worker for C++ model update)
  const setProperty = useCallback((elementId: string, properties: Record<string, unknown>) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "setProperty", elementId, properties });
  }, []);

  // Auto-layout command (Phase 18)
  const autoLayout = useCallback((selectedOnly = false, algorithm = 0) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "autoLayout", selectedOnly, algorithm });
  }, []);

  // Add connection between elements (BPMN: sequence flow, Node: socket connection)
  const addConnection = useCallback(
    (sourceId: number, targetId: number, sourceSocket?: string, targetSocket?: string) => {
      if (!workerRef.current) return;
      if (editorType === "bpmn") {
        workerRef.current.postMessage({ type: "addSequenceFlow", sourceId, targetId });
      } else {
        workerRef.current.postMessage({
          type: "connect",
          sourceGroup: sourceId,
          sourceSocket: sourceSocket ?? "out",
          targetGroup: targetId,
          targetSocket: targetSocket ?? "in",
        });
      }
    },
    [editorType],
  );

  // Validation command (Phase 15, Plan 09 -- gap closure)
  const validate = useCallback(() => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "validate" });
  }, []);

  // Inline text editing commands (Phase 15, Plan 09 -- gap closure)
  const startInlineEdit = useCallback((groupId: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "startInlineEdit", groupId });
  }, []);

  const commitInlineEdit = useCallback((text: string) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "commitInlineEdit", text });
  }, []);

  const cancelInlineEdit = useCallback(() => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "cancelInlineEdit" });
  }, []);

  // Condition and marker commands (Phase 15, Plan 09 -- gap closure)
  const setFlowCondition = useCallback((connectionId: number, expression: string) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "setFlowCondition", connectionId, expression });
  }, []);

  const setDefaultFlow = useCallback((connectionId: number, isDefault: boolean) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "setDefaultFlow", connectionId, isDefault });
  }, []);

  const setMultiInstance = useCallback((groupId: number, multiInstanceType: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "setMultiInstance", groupId, multiInstanceType });
  }, []);

  // Structural BPMN commands (Phase 15, Plan 10 -- gap closure)
  const attachBoundaryEvent = useCallback((eventId: number, parentId: number, position = 0.625) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "attachBoundaryEvent", eventId, parentId, position });
  }, []);

  const detachBoundaryEvent = useCallback((eventId: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "detachBoundaryEvent", eventId });
  }, []);

  const toggleSubprocess = useCallback((subprocessId: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "toggleSubprocess", subprocessId });
  }, []);

  const createPool = useCallback((x: number, y: number, name = "Pool") => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "createPool", x, y, name });
  }, []);

  const createLane = useCallback((poolId: number, name = "Lane") => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "createLane", poolId, name });
  }, []);

  // Dynamic socket management (Phase 15, Plan 11 -- gap closure)
  const addSocket = useCallback(
    (groupId: number, socketId: string, name: string, dataType: string, isInput: boolean) => {
      if (!workerRef.current) return;
      workerRef.current.postMessage({
        type: "addSocket",
        groupId,
        socketId,
        name,
        dataType,
        isInput,
      });
    },
    [],
  );

  const removeSocket = useCallback((groupId: number, socketId: string, isInput: boolean) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "removeSocket", groupId, socketId, isInput });
  }, []);

  // Node grouping (Phase 15, Plan 11 -- gap closure)
  const groupSelected = useCallback((label = "Group") => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "groupSelected", label });
  }, []);

  const dissolveGroup = useCallback((groupId: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "dissolveGroup", groupId });
  }, []);

  // Collapse/expand toggle for node groups (Phase 26, Plan 02)
  const toggleCollapse = useCallback((groupId: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "toggleCollapse", groupId });
  }, []);

  // Query hovered group for tooltip display (Phase 26, Plan 02)
  const queryHoveredGroup = useCallback((screenX: number, screenY: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "queryHoveredGroup", screenX, screenY });
  }, []);

  // Connection mode commands (Phase 24, Plan 03)
  const setToolModeOnWorker = useCallback((mode: number) => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "setToolMode", mode });
  }, []);

  const cancelConnectDrag = useCallback(() => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "cancelConnectDrag" });
  }, []);

  const createAndConnect = useCallback(
    (
      elementType: string,
      x: number,
      y: number,
      label: string,
      sourceId: number,
      sourceSocket = "",
    ) => {
      if (!workerRef.current) return;
      workerRef.current.postMessage({
        type: "createAndConnect",
        elementType,
        x,
        y,
        label,
        sourceId,
        sourceSocket,
      });
    },
    [],
  );

  return {
    isReady,
    error,
    initEditor,
    stop,
    resize,
    forwardInput,
    addElement,
    undo,
    redo,
    deleteSelected,
    copy,
    cut,
    paste,
    selectAll,
    zoom,
    fitToView,
    loadDocument,
    saveDocument,
    importN8N,
    setProperty,
    autoLayout,
    addConnection,
    validate,
    startInlineEdit,
    commitInlineEdit,
    cancelInlineEdit,
    setFlowCondition,
    setDefaultFlow,
    setMultiInstance,
    attachBoundaryEvent,
    detachBoundaryEvent,
    toggleSubprocess,
    createPool,
    createLane,
    addSocket,
    removeSocket,
    groupSelected,
    dissolveGroup,
    setToolModeOnWorker,
    cancelConnectDrag,
    createAndConnect,
    toggleCollapse,
    queryHoveredGroup,
    workerId: workerIdRef.current,
  };
}
